import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { ClipboardList, Eye, Trash2 } from "lucide-react";

/**
 * RequestsTable — Tabla de "Listado de Solicitudes" + diálogo de confirmación de borrado.
 *
 * Extraída 1:1 desde `pages/RequestsPage.js`. Renderiza las solicitudes ya filtradas
 * (número, título, cliente, prioridad, estado, asignado, fecha) con acciones de fila
 * (ver detalle y eliminar con guard RBAC `canDelete`). Incluye el `AlertDialog` de borrado.
 *
 * @param {Array}    props.filteredRequests   Solicitudes ya filtradas por búsqueda/estado/prioridad.
 * @param {Object}   props.statusColors       Mapa estado→clases de color del badge.
 * @param {Object}   props.statusLabels       Mapa estado→etiqueta legible.
 * @param {Object}   props.priorityColors     Mapa prioridad→clases de color del badge.
 * @param {Object}   props.priorityLabels     Mapa prioridad→etiqueta legible.
 * @param {Function} props.formatDate         (dateStr) => string — formatea fechas.
 * @param {Function} props.openDetailModal    (request) => void — abre el modal de detalle.
 * @param {Function} props.canDelete          (resource) => boolean — permiso RBAC para eliminar.
 * @param {Function} props.openDeleteDialog   (event, request) => void — abre el diálogo de borrado.
 * @param {boolean}  props.deleteDialogOpen   Apertura del diálogo de borrado.
 * @param {Function} props.setDeleteDialogOpen Setter del flag del diálogo de borrado.
 * @param {Object|null} props.requestToDelete Solicitud pendiente de borrado (para el texto).
 * @param {Function} props.handleDeleteRequest Confirma el borrado permanente.
 */
export const RequestsTable = ({
  filteredRequests,
  statusColors,
  statusLabels,
  priorityColors,
  priorityLabels,
  formatDate,
  openDetailModal,
  canDelete,
  openDeleteDialog,
  deleteDialogOpen,
  setDeleteDialogOpen,
  requestToDelete,
  handleDeleteRequest,
}) => {
  return (
    <>
      {/* Requests Table */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardHeader>
          <CardTitle className="text-white font-heading flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-[#03BEE4]" />
            Listado de Solicitudes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1E1E5F]">
                  <TableHead className="text-slate-400">Número</TableHead>
                  <TableHead className="text-slate-400">Título</TableHead>
                  <TableHead className="text-slate-400">Cliente</TableHead>
                  <TableHead className="text-slate-400">Prioridad</TableHead>
                  <TableHead className="text-slate-400">Estado</TableHead>
                  <TableHead className="text-slate-400">Asignado a</TableHead>
                  <TableHead className="text-slate-400">Fecha</TableHead>
                  <TableHead className="text-slate-400 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-400 py-8">
                      No hay solicitudes que coincidan con los filtros
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((request) => (
                    <TableRow key={request.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]/50">
                      <TableCell className="text-[#03BEE4] font-mono text-sm font-semibold">
                        {request.request_number}
                      </TableCell>
                      <TableCell className="text-white max-w-xs truncate">
                        {request.title}
                      </TableCell>
                      <TableCell className="text-slate-300">
                        {request.customer_name}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${priorityColors[request.priority]} text-white`}>
                          {priorityLabels[request.priority]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusColors[request.status]} text-white`}>
                          {statusLabels[request.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {request.assigned_to_name || <span className="text-slate-500 italic">Sin asignar</span>}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {formatDate(request.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetailModal(request)}
                            className="text-[#03BEE4] hover:text-[#03BEE4]/80"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                          {canDelete("requests") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => openDeleteDialog(e, request)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                              data-testid={`delete-request-${request.request_number}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A35] border-[#1E1E5F]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar solicitud?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Esta acción eliminará permanentemente la solicitud <span className="text-[#42E695] font-semibold">{requestToDelete?.request_number}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#1E1E5F] text-slate-300 hover:bg-[#1E1E5F]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRequest}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
